import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { outputTypeDisplayName, uomKeyForOutputItemType } from '@/tools/models/outputItemMetadata.utilities';

interface OutputItemMetadataLabelProps {
  /** Wire-format item type from the derived feature (e.g. "Signal", "Scalar", "Condition"). */
  outputItemType?: string;
  /**
   * Wire-format metadata map from the source derived/input feature. `undefined` alongside an
   * undefined `outputItemType` means "no restriction at all" (renders "Any"). An input feature can
   * legitimately have `outputItemType` set with `outputItemMetadata` left `undefined` (a type-only
   * restriction, any UOM). That case still renders the type.
   */
  outputItemMetadata?: Record<string, string>;
  /** Omit the bold "Metadata:" prefix, for callers that supply their own label context. */
  hidePrefix?: boolean;
}

const uomLabel = (uom: string | undefined, t: (key: string) => string): string => {
  if (uom === undefined) return t('TEMPLATES.ANY_UOM_LABEL');
  if (uom === '') return t('TEMPLATES.DIMENSIONLESS_UOM_LABEL');
  return uom;
};

const buildTypeAndUomLabel = (
  outputItemType: string | undefined,
  outputItemMetadata: Record<string, string>,
  t: (key: string) => string,
): string | undefined => {
  const displayName = outputTypeDisplayName(outputItemType);
  if (displayName === undefined) {
    return undefined;
  }
  // The UOM key (ValueUom for Signal/Measure, Uom for Scalar, none for Condition) is the single
  // rule shared with the metadata projection.
  const uomKey = uomKeyForOutputItemType(outputItemType);
  if (uomKey === undefined) {
    return displayName;
  }
  return `${displayName} (${uomLabel(outputItemMetadata[uomKey], t)})`;
};

/**
 * Inline annotation describing a model derived/input feature's output item restriction. Rendered
 * with a bold "Metadata:" prefix that matches the "Name:" prefix used by `FormulaItemTable` rows.
 *
 *  - both fields `undefined` → renders "Any" (no restriction).
 *  - `outputItemType` set (with `outputItemMetadata` a map, or left `undefined` for a type-only,
 *    any-UOM restriction) → renders the type plus the UOM annotation when applicable.
 *
 * Renders nothing only when a type is present but unrecognised (defensive fallback).
 */
export const OutputItemMetadataLabel = ({
  outputItemType,
  outputItemMetadata,
  hidePrefix = false,
}: OutputItemMetadataLabelProps) => {
  const { t } = useTranslation();

  // Input features may have a type without metadata, so only treat them as unrestricted when both are unset.
  const value =
    outputItemType === undefined && outputItemMetadata === undefined
      ? t('TEMPLATES.ANY_TYPE_LABEL')
      : buildTypeAndUomLabel(outputItemType, outputItemMetadata ?? {}, t);

  if (value === undefined) {
    return null;
  }

  return (
    <span data-testid="outputItemMetadataLabel">
      {!hidePrefix && <span {...stylex.props(styles.label)}>{t('TEMPLATES.METADATA_LABEL')}:</span>} {value}
    </span>
  );
};

const styles = stylex.create({ label: { fontWeight: 700 } });
